/**
 * AI Actions settings section.
 *
 * Provides controls for AI action behavior, including the Success
 * Confirmation toggle. Calls useProfile() directly to manage state.
 */

import { useCallback } from 'react';

import { useProfile } from '@/hooks/profile/useProfile';
import { ButtonList } from '@/lib/base/button-list';
import { Toggle } from '@/lib/base/forms/toggle';
import { useViewHealthMarker } from '@/lib/view-health';

export function AiActionsSection() {
  useViewHealthMarker('settings-section-ready');
  const { formData, successConfirmationSaving, setSuccessConfirmationEnabled } =
    useProfile();

  const onSuccessConfirmationToggle = useCallback(
    (enabled: boolean) => {
      setSuccessConfirmationEnabled(enabled).catch(() => {
        // Error is already set in useProfile state
      });
    },
    [setSuccessConfirmationEnabled]
  );

  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>AI Actions</h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Configure AI action behavior.
        </p>
      </div>

      <div className='space-y-6'>
        <ButtonList aria-label='AI action settings'>
          <ButtonList.Item
            trailing={
              <Toggle
                isSelected={formData.show_proposal_success_state}
                onChange={onSuccessConfirmationToggle}
                isDisabled={successConfirmationSaving}
                aria-label='Toggle success confirmation for AI actions'
              />
            }
          >
            <p className='text-sm font-medium text-fg-primary mb-0.5'>
              Success confirmation
            </p>
            <p className='text-sm text-fg-tertiary'>
              Show confirmation screen after completing proposals
            </p>
          </ButtonList.Item>
        </ButtonList>
      </div>
    </div>
  );
}
